/**
 * Assessment library — canned wording + clause-specific Initial Findings
 * Assessment options, aligned to the CER portal surface.
 *
 * This is what QA rule `assessment-alignment` (backend/src/qa) checks
 * non-conformance write-ups against, and what the admin UI offers as
 * pick-lists when reviewing a submission.
 *
 * Data source: the shared ClauseLibrary, generated from the technical head's
 * recommendation wording docs. Items with no canned paragraph ship with an
 * empty `wordingOptions` — the inspector writes custom wording for those
 * (the dashboard marks it as a Custom Response).
 *
 * Related: clause-library.ts holds the FIELD-side view of the same doc
 * lineage (question text + recommendations keyed by section for the PWA).
 * This module is the QA/portal-side view keyed by section + checklistItemId,
 * with assessment options attached. The option source should be replaced
 * with scraped portal data once the DOM inspection pass is complete.
 */
import { z } from 'zod';
import { ASSESSMENT_LIBRARY_DATA } from './assessment-library.data.js';
import { ClauseLibrary } from './clause-library.js';
import { SECTION5_PORTAL_ASSESSMENT_VALUES } from './section5-portal-options.js';
import { SECTION4_PORTAL_ASSESSMENT_VALUES } from './section4-portal-options.js';
import { PV_PORTAL_ASSESSMENT_VALUES } from './pv-portal-options.js';
import { AssessmentOptionSchema, ChecklistSectionSchema, } from './field.js';
export { GENERAL_RECOMMENDATION_WORDING } from './assessment-library.data.js';
/**
 * PLACEHOLDER portal-aligned severity set, applied to every item for now.
 * Inspector feedback says the portal's severity dropdown varies PER checklist
 * item — the per-item values need confirming against the live portal (DOM
 * inspection pass) before upload automation can trust them. Until then this
 * uniform set drives the admin QA pick-list and the alignment warn-check.
 */
export const DEFAULT_SEVERITY_OPTIONS = [
    'Imminent risk',
    'Potential risk',
    'Non-conformance (no safety risk)',
    'Compliant',
];
export const ASSESSMENT_OPTIONS = {
    compliant: { value: 'compliant', label: 'Compliant', missingPhotoExempt: true },
    notCited: { value: 'not_cited', label: 'Not Sighted', missingPhotoExempt: true },
    nonRegulatory: {
        value: 'non_regulatory',
        label: 'Non Regulatory',
        requiresPhoto: true,
    },
    notApplicable: { value: 'not_applicable', label: 'Not Applicable', missingPhotoExempt: true },
    // Minor findings never need evidence attached (technical head Aug 2026 ruling,
    // raised against "SB & Labelling 5", stated for minor severity generally).
    // CER-58 dashboard missing-photo warn/override also exempts them.
    minorNonCompliance: {
        value: 'minor_non_compliance',
        label: 'Minor Non Compliance',
        missingPhotoExempt: true,
    },
    mediumNonCompliance: {
        value: 'medium_non_compliance',
        label: 'Medium Non Compliance',
        requiresPhoto: true,
    },
    rectificationWork: {
        value: 'rectification_work',
        label: 'Rectification Work',
        requiresPhoto: true,
    },
    unsafe: { value: 'unsafe', label: 'Unsafe', requiresPhoto: true },
    unsafeProductRecall: {
        value: 'unsafe_product_recall',
        label: 'Unsafe due to Product Recall',
        requiresPhoto: true,
    },
    unsafeThirdParty: {
        value: 'unsafe_third_party',
        label: 'Unsafe due to Third Party',
        requiresPhoto: true,
    },
    inverterSettings: {
        value: 'inverter_settings',
        label: 'Inverter settings',
    },
    /**
     * "Not viewed" — the inspector could not see every inverter setting, so the
     * item is neither a pass nor a finding. Requested for the GENERAL 6.x
     * inverter-configuration items, which previously offered `inverter_settings`
     * as their ONLY choice: an inspector who had verified the settings had no
     * way to say so, and one who couldn't see them had no way to say that either.
     *
     * Portal-safe to add: the portal's own "Initial findings assessment" select
     * is `neverFill` (see CerPortalPayloadV1), so no value here is ever typed
     * into CER — it drives our report and QA only.
     */
    notViewed: {
        value: 'not_viewed',
        label: 'N/V (not viewed all settings)',
        missingPhotoExempt: true,
    },
};
const DOCUMENTATION_OPTIONS = [
    ASSESSMENT_OPTIONS.compliant,
    ASSESSMENT_OPTIONS.notCited,
    ASSESSMENT_OPTIONS.mediumNonCompliance,
];
const EQUIPMENT_OPTIONS = [
    ASSESSMENT_OPTIONS.compliant,
    ASSESSMENT_OPTIONS.rectificationWork,
];
const DEFAULT_CLAUSE_OPTIONS = [
    ASSESSMENT_OPTIONS.compliant,
    ASSESSMENT_OPTIONS.notApplicable,
    ASSESSMENT_OPTIONS.nonRegulatory,
    ASSESSMENT_OPTIONS.minorNonCompliance,
    ASSESSMENT_OPTIONS.mediumNonCompliance,
    ASSESSMENT_OPTIONS.rectificationWork,
];
export const AssessmentLibraryEntrySchema = z.object({
    /** Which checklist book this item belongs to. Clause IDs are not globally unique. */
    section: ChecklistSectionSchema,
    /** Checklist item id exactly as printed, e.g. "SB & Labelling 13", "Pre-assembled BS 78". */
    checklistItemId: z.string().min(1),
    /** Standard clause citations, e.g. ["AS/NZS 4777.1:2024 Clause 5.3.6.4"]. */
    clauses: z.array(z.string()),
    /** The Compliant question for the item. */
    question: z.string().min(1),
    /** Canned assessment paragraphs — zero or more; verbatim from the doc. */
    wordingOptions: z.array(z.string()),
    /** Portal-aligned severity values selectable for this item. */
    severityOptions: z.array(z.string()),
    /** Clause-specific portal Initial Findings Assessment options. */
    assessmentOptions: z.array(AssessmentOptionSchema),
});
const clauseLibraryAssessmentEntries = ClauseLibrary.map((raw) => ({
    section: raw.section,
    checklistItemId: raw.clauseId,
    clauses: splitStandardRefs(raw.standardRef),
    question: raw.question,
    wordingOptions: [...raw.cannedRecommendations],
    // TODO(M3): remove once all callers read assessmentOptions. These are kept
    // only for legacy submission compatibility during the assessment refactor.
    severityOptions: [...DEFAULT_SEVERITY_OPTIONS],
    assessmentOptions: [...deriveAssessmentOptions(raw.section, raw.clauseId)],
}));
const existingKeys = new Set(clauseLibraryAssessmentEntries.map((entry) => lookupKey(entry.section, entry.checklistItemId)));
const section5AssessmentOnlyEntries = ASSESSMENT_LIBRARY_DATA.filter((raw) => !existingKeys.has(lookupKey('battery-section-5-dc', raw.checklistItemId))).map((raw) => ({
    section: 'battery-section-5-dc',
    checklistItemId: raw.checklistItemId,
    clauses: [...raw.clauses],
    question: raw.question,
    wordingOptions: [...raw.wordingOptions],
    severityOptions: [...DEFAULT_SEVERITY_OPTIONS],
    assessmentOptions: [...deriveAssessmentOptions('battery-section-5-dc', raw.checklistItemId)],
}));
/** Every entry, in clause-library order, with Section 5 assessment-only legacy rows appended. */
export const ASSESSMENT_LIBRARY = [
    ...clauseLibraryAssessmentEntries,
    ...section5AssessmentOnlyEntries,
];
/** section|checklistItemId-keyed lookup map. Memoised at module init. */
const lookupBySection = new Map();
const legacyLookup = new Map();
for (const entry of ASSESSMENT_LIBRARY) {
    lookupBySection.set(lookupKey(entry.section, entry.checklistItemId), entry);
    // Preserve the pre-Section-4 behaviour for legacy single-argument calls:
    // prefer Section 5 when IDs collide because it was the original dataset.
    if (!legacyLookup.has(entry.checklistItemId) || entry.section === 'battery-section-5-dc') {
        legacyLookup.set(entry.checklistItemId, entry);
    }
}
export function findAssessmentEntry(sectionOrChecklistItemId, checklistItemId) {
    if (checklistItemId !== undefined) {
        return lookupBySection.get(lookupKey(sectionOrChecklistItemId, checklistItemId));
    }
    return legacyLookup.get(sectionOrChecklistItemId.trim());
}
export function assessmentOptionsForClause(section, checklistItemId) {
    return findAssessmentEntry(section, checklistItemId)?.assessmentOptions ?? DEFAULT_CLAUSE_OPTIONS;
}
export function findAssessmentOption(section, checklistItemId, value) {
    if (!value)
        return undefined;
    return assessmentOptionsForClause(section, checklistItemId).find((option) => option.value === value);
}
export function assessmentValueForClauseResponse(response) {
    return response.assessment ?? legacyAssessmentValue(response);
}
export function assessmentLabelForValue(value) {
    if (!value)
        return undefined;
    return Object.values(ASSESSMENT_OPTIONS).find((option) => option.value === value)?.label;
}
export function legacyFieldsForAssessment(value) {
    switch (value) {
        case ASSESSMENT_OPTIONS.compliant.value:
            return { outcome: 'Compliant' };
        case ASSESSMENT_OPTIONS.notApplicable.value:
        case ASSESSMENT_OPTIONS.notCited.value:
        // N/V is an absence of observation, not a finding — it must not fall
        // through to the Non-Compliant default, or the clause would demand
        // recommendation wording for something the inspector never saw.
        case ASSESSMENT_OPTIONS.notViewed.value:
            return { outcome: 'Not Applicable' };
        default:
            return { outcome: 'Non-Compliant', severity: assessmentLabelForValue(value) };
    }
}
export function assessmentRequiresWording(value) {
    if (!value)
        return false;
    return legacyFieldsForAssessment(value).outcome === 'Non-Compliant';
}
/**
 * Write the assessment and the legacy outcome/severity that goes with it.
 *
 * Compliant, Not Applicable, Not Sighted and N/V do not require wording, so a
 * previous severity and recommendation are dropped. Switching between two
 * findings keeps the wording the inspector already entered.
 */
export function applyAssessment(response, value) {
    const legacy = legacyFieldsForAssessment(value);
    if (assessmentRequiresWording(value)) {
        return { ...response, assessment: value, ...legacy };
    }
    const { severity: _severity, recommendation: _recommendation, ...rest } = response;
    return { ...rest, assessment: value, ...legacy };
}
export function legacyAssessmentValue(response) {
    if (response.outcome === 'Compliant')
        return ASSESSMENT_OPTIONS.compliant.value;
    if (response.outcome === 'Not Applicable')
        return ASSESSMENT_OPTIONS.notApplicable.value;
    if (response.outcome !== 'Non-Compliant')
        return undefined;
    const severity = response.severity?.trim().toLowerCase();
    if (!severity)
        return undefined;
    if (severity.includes('rectification'))
        return ASSESSMENT_OPTIONS.rectificationWork.value;
    if (severity.includes('product recall'))
        return ASSESSMENT_OPTIONS.unsafeProductRecall.value;
    if (severity.includes('third party') && severity.includes('unsafe')) {
        return ASSESSMENT_OPTIONS.unsafeThirdParty.value;
    }
    if (severity.includes('unsafe') || severity.includes('imminent'))
        return ASSESSMENT_OPTIONS.unsafe.value;
    if (severity.includes('medium'))
        return ASSESSMENT_OPTIONS.mediumNonCompliance.value;
    if (severity.includes('minor'))
        return ASSESSMENT_OPTIONS.minorNonCompliance.value;
    return undefined;
}
function lookupKey(section, checklistItemId) {
    return `${section}|${checklistItemId.trim()}`;
}
function deriveAssessmentOptions(section, checklistItemId) {
    if (section === 'battery-section-5-dc') {
        const values = SECTION5_PORTAL_ASSESSMENT_VALUES[checklistItemId];
        if (!values)
            throw new Error(`Missing Section 5 portal options for ${checklistItemId}`);
        return values.map((value) => {
            const option = Object.values(ASSESSMENT_OPTIONS).find((candidate) => candidate.value === value);
            if (!option)
                throw new Error(`Unknown Section 5 portal assessment value: ${value}`);
            return option;
        });
    }
    if (section === 'battery-section-4-ac') {
        return optionsFromPortalValues(SECTION4_PORTAL_ASSESSMENT_VALUES, checklistItemId, 'Section 4');
    }
    if (section === 'cer-pv') {
        return optionsFromPortalValues(PV_PORTAL_ASSESSMENT_VALUES, checklistItemId, 'PV');
    }
    if (/^Documentation\b/i.test(checklistItemId))
        return DOCUMENTATION_OPTIONS;
    if (/^(?:Pre-assembled BS|Integrated BESS)\b/i.test(checklistItemId))
        return EQUIPMENT_OPTIONS;
    return DEFAULT_CLAUSE_OPTIONS;
}
function optionsFromPortalValues(source, checklistItemId, sectionLabel) {
    const values = source[checklistItemId];
    if (!values)
        throw new Error(`Missing ${sectionLabel} portal options for ${checklistItemId}`);
    return resolvePortalOptions(values, sectionLabel);
}
function resolvePortalOptions(values, sectionLabel) {
    return values.map((value) => {
        const option = Object.values(ASSESSMENT_OPTIONS).find((candidate) => candidate.value === value);
        if (!option)
            throw new Error(`Unknown ${sectionLabel} portal assessment value: ${value}`);
        return option;
    });
}
function splitStandardRefs(refs) {
    if (refs === 'Source document did not specify a standard reference')
        return [];
    return refs
        .split(';')
        .map((part) => part.trim())
        .filter(Boolean);
}
/** Multiple complete canned paragraphs are still library wording. */
export function isCustomResponse(section, clauseId, wording) {
    let remaining = wording?.trim() ?? '';
    if (!remaining)
        return false;
    const options = [...(findAssessmentEntry(section, clauseId)?.wordingOptions ?? [])]
        .filter((option) => option.trim())
        .sort((a, b) => b.length - a.length);
    while (remaining) {
        const match = options.find((option) => remaining.startsWith(option.trim()) &&
            (remaining.length === option.trim().length ||
                /^\s/.test(remaining.slice(option.trim().length))));
        if (!match)
            return true;
        remaining = remaining.slice(match.trim().length).trim();
    }
    return false;
}
//# sourceMappingURL=assessment-library.js.map